import styles from './addDeviceModelModal.module.css';
import textFieldStyles from 'ui/TextField/textField.module.css';

import attentionIcon from 'assets/svg/attention-01.svg';

import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useForm, FormProvider } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import classNames from 'classnames';
import { address_server_short, PUBLIC_ASSETS } from '../../../config';
import { getAllUserFields } from 'services/list.services';
import { devicesStore } from 'store/mobx/devices.store';
import { userFieldCategory } from 'consts/userField.consts';
import useAlert from 'hooks/useAlert';
import { UserFields } from 'components/UserFields';
import { MultiItemList } from 'components/Widgets/MultiInputList';
import Modal from '../../../ui/Modal';
import FormTextField from 'ui/formItems/FormTextField';

const AddDeviceModelModal = observer(({ setModalOpened, mode }) => {
  const { showAlert } = useAlert();
  const { setSelectedModel, upsertDeviceModel, selectedDeviceModel, icons, setIcons } =
    devicesStore;
  const [deviceDetails, setDeviceDetails] = useState([]);
  const userFieldsRef = useRef(null);

  const form = useForm({
    defaultValues: {
      name: selectedDeviceModel?.name ?? '',
      description: selectedDeviceModel?.description ?? '',
      image: selectedDeviceModel?.image ?? '',
      userFields: selectedDeviceModel?.userFields ?? undefined,
      specifications: selectedDeviceModel?.specifications ?? [{ name: '', value: '' }],
    },
  });

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    register,
    control,
  } = form;

  useEffect(() => {
    setIcons();
  }, []);

  useEffect(() => {
    setValue('specifications', deviceDetails);
  }, [deviceDetails, setValue]);

  const closeModalClick = () => {
    setModalOpened(false);
  };

  const submitClick = async (data) => {
    if (
      !data.name?.trim() ||
      !data.image?.trim() ||
      data.specifications?.some((item) => !(item?.name?.trim() && item?.value?.trim()))
    )
      return;

    if (selectedDeviceModel?.id != null) dataWillBeChangedAlert();

    setSelectedModel({
      ...selectedDeviceModel,
      name: data.name,
      description: data.description?.length ? data.description : undefined,
      image: data.image,
      user_fields: data.userFields?.length ? data.userFields : undefined,
      specifications: data.specifications?.length ? data.specifications : undefined,
    });
    const result = await upsertDeviceModel();

    if (!result.success) {
      errorCommonAlert(result.error_code, result.error);
    } else {
      successUpsertDeviceModelAlert();
      closeModalClick();
    }
  };

  const changeItemByIndex = (key, value, index) => {
    let res = JSON.parse(JSON.stringify(deviceDetails));
    let item = res[index];
    if (item) {
      item[key] = value;
    }
    setDeviceDetails(res);
  };

  const addItem = () => {
    let res = JSON.parse(JSON.stringify(deviceDetails));
    res.push({ name: '', value: '' });
    setDeviceDetails(res);
  };

  const deleteItem = (index) => {
    let res = JSON.parse(JSON.stringify(deviceDetails));
    res.splice(index, 1);
    setDeviceDetails(res);
  };

  const errorCommonAlert = (code, message) => {
    showAlert(` Код № ${code}${message ? ` - ${message}` : ''}!`, 'error');
  };

  const dataWillBeChangedAlert = () =>
    showAlert(`Данные модели оборудования будут изменены!`, 'warning');

  const successUpsertDeviceModelAlert = () =>
    showAlert(
      selectedDeviceModel?.id != null
        ? `Модель оборудования успешно изменена!`
        : 'Модель оборудования добавлена успешно!',
      'success',
    );

  const Item = (value, index) => {
    return (
      <div key={index} className={styles.itemRow}>
        <div className={styles.textField}>
          <Controller
            name={`specifications.${index}.name`}
            control={form.control}
            rules={{ required: ' ' }}
            render={({ field, fieldState }) => (
              <div className={styles.inputContainer}>
                <div>Название</div>
                <input
                  {...field}
                  className={styles.input}
                  value={value.name}
                  onChange={(e) => changeItemByIndex('name', e.target.value, index)}
                />
                {fieldState.error && !value.name?.trim() && (
                  <div className={styles.dataErrorBlock}>
                    <img src={attentionIcon} alt="attention" />
                    <div className={styles.dataError}>
                      {fieldState.error.message && 'Поле обязательно для заполнения'}
                    </div>
                  </div>
                )}
              </div>
            )}
          />
        </div>
        <div className={styles.textField}>
          <Controller
            name={`specifications.${index}.value`}
            control={form.control}
            rules={{ required: ' ' }}
            render={({ field, fieldState }) => (
              <div className={styles.inputContainer}>
                <div>Значение</div>
                <input
                  {...field}
                  className={styles.input}
                  value={value.value}
                  onChange={(e) => changeItemByIndex('value', e.target.value, index)}
                />
                <div className={styles.buttonClear} onClick={() => deleteItem(index)}>
                  <div className={'close_icon'}></div>
                </div>
                {fieldState.error && !value.value?.trim() && (
                  <div className={styles.dataErrorBlock}>
                    <img src={attentionIcon} alt="attention" />
                    <div className={styles.dataError}>
                      {fieldState.error.message && 'Поле обязательно для заполнения'}
                    </div>
                  </div>
                )}
              </div>
            )}
          />
        </div>
      </div>
    );
  };

  return (
    <Modal
      title="Добавить модель оборудования"
      onSubmit={handleSubmit(submitClick)}
      onClose={closeModalClick}
    >
      <FormProvider {...form}>
        <div className={styles.modalBody}>
          <FormTextField name="name" label={'Наименование'} required />
          <FormTextField label="Описание" name="description" />
          <Controller
            name="image"
            control={control}
            rules={{ required: 'Поле обязательно для заполнения' }}
            render={({ field }) => (
              <div className={classNames(textFieldStyles.formField, styles.iconsField)}>
                <div className={styles.formFieldContainer}>
                  <div className={textFieldStyles.label}>{'Иконка'}</div>
                  <div className={styles.iconsContainer}>
                    {icons.map((url, index) => (
                      <div
                        key={index}
                        className={classNames(
                          { [styles.bg_blue]: url === field.value },
                          styles.itemIcon,
                        )}
                        onClick={() => {
                          field.onChange(url);
                        }}
                      >
                        <div className={styles.containerImg}>
                          <img
                            src={address_server_short + PUBLIC_ASSETS + url}
                            className={styles.itemImg}
                            alt={'img'}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {errors.image && (
                  <div className={styles.dataErrorBlock}>
                    <img src={attentionIcon} alt={'attention'} />
                    <div className={textFieldStyles.dataError}>{errors.image.message}</div>
                  </div>
                )}
              </div>
            )}
          />
          <p className={styles.categoryTitle}>Пользовательские параметры</p>
          <Controller
            name="userFields"
            control={control}
            render={() => (
              <UserFields
                ref={userFieldsRef}
                onChange={(fields) => setValue('userFields', fields)}
                mode={mode}
                category={userFieldCategory.device}
                loadApi={(options) => getAllUserFields(options, 'device')}
              />
            )}
          />
          <p className={styles.categoryTitle}>Характеристики</p>
          <div>
            <Controller
              name="specifications"
              control={control}
              render={() => (
                <MultiItemList
                  items={deviceDetails}
                  onChange={addItem}
                  Item={Item}
                  confirmButton={'Добавить поле'}
                />
              )}
            />
          </div>
        </div>
      </FormProvider>
    </Modal>
  );
});

export default AddDeviceModelModal;
